'use client';

import { Card, HorizontalTabs, Shape } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import { useI18n } from '~/features/i18n';
import { renderRichText } from '~/storyblok/utils/RichText';
import type { StoryblokStory } from 'storyblok-generate-ts';
import type { SB_NewsProps } from '../types.generated';

export const _NewsSectionWithState: React.FC<{
    newsItems: StoryblokStory<SB_NewsProps>[];
    tabs: Array<{
        label: string;
        value: string;
    }>;
}> = props => {
    const i18n = useI18n();
    const tabState = HorizontalTabs.useState(
        [
            {
                value: 'all',
                label: i18n._('All messages'),
            },
            ...props.tabs,
        ] as const,
        'all',
    );

    return (
        <section className="bg-brown-100 py-1 lg:py-12">
            <Shape
                type="dot"
                className="absolute top-400 -left-20 z-0 scale-75 md:-left-16 lg:-left-12 lg:scale-100"
            />

            <header className="container mx-auto mt-6 mb-8 flex justify-center">
                <div className="w-full max-lg:max-w-xl">
                    <HorizontalTabs.StateLess {...tabState} />
                </div>
            </header>

            <main className="container mx-auto grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-10">
                {props.newsItems
                    ?.filter((item): item is Exclude<typeof item, string> =>
                        typeof item === 'string'
                            ? false
                            : tabState.value === 'all'
                              ? true
                              : item.content.category === tabState.value,
                    )
                    ?.map((item, index) => (
                        <div
                            key={item.uuid}
                            className={cx(
                                'col-span-1 mx-auto max-w-xl lg:mx-0 lg:max-w-none',
                                calculateColSpan(index),
                            )}
                        >
                            <Card
                                href={`/${item.full_slug}`}
                                title={item.content.title}
                                subtitle={new Date(
                                    item.published_at || item.created_at,
                                ).toLocaleDateString('nl-NL', {
                                    year: 'numeric',
                                    month: 'long',
                                })}
                                image={item.content.featuredImage?.filename || undefined}
                                imageClassName={cx(
                                    'object-cover',
                                    index % 6 === 3 || index % 6 === 4 || index % 6 === 5
                                        ? 'mb-8 aspect-1100/700 lg:mb-8'
                                        : index % 6 === 2
                                          ? 'max-h-[400px] w-full'
                                          : index % 6 === 0
                                            ? 'mb-8 aspect-1000/600 lg:mb-8'
                                            : index % 6 === 1
                                              ? 'mb-8 lg:mb-8'
                                              : null,
                                )}
                                cardClassName="h-full bg-white p-2"
                                description={
                                    item.content.intro && renderRichText(item.content.intro as any)
                                }
                                tag={item.content.category.toString()}
                                flexImageText={
                                    index % 6 === 2
                                        ? 'flex flex-col gap-8 xl:gap-0 xl:flex-row'
                                        : 'flex flex-col'
                                }
                                allTextClassName={
                                    index % 6 === 2 ? 'lg:flex flex-col justify-center' : ''
                                }
                            />
                        </div>
                    ))}
            </main>
        </section>
    );
};

const calculateColSpan = (index: number) => {
    switch (index % 6) {
        case 0:
            return 'lg:col-span-2';
        case 1:
        case 3:
        case 4:
        case 5:
            return 'lg:col-span-1';
        case 2:
            return 'lg:col-span-3';
    }
};
